import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import PrivateRoute from './components/auth/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { DepartmentProvider } from './contexts/DepartmentContext';
import { FullScreenProvider } from './contexts/FullScreenContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OmnichannelProvider } from './contexts/OmnichannelContext';
import { TenantProvider } from './contexts/TenantContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TicketRefreshProvider } from './contexts/TicketRefreshContext';
import { ToastProvider } from './contexts/ToastContext';
import './i18n';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ContactPage from './pages/ContactPage';
import CreateTicketPage from './pages/CreateTicketPage';
import EnhancedDashboardPage from './pages/EnhancedDashboardPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import MyMemoPage from './pages/MyMemoPage';
import MyWorkspacePage from './pages/MyWorkspacePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import Registration from './pages/Registration';
import ReportPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import TicketDetailPage from './pages/TicketDetailPage';
import TicketsPage from './pages/TicketsPage';
import VerifyResetPasswordPage from './pages/VerifyResetPasswordPage';
import OmnichannelPage from './pages/OmnichannelPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage360 from './pages/CustomerDetailPage360';
import ProfilePage from './pages/ProfilePage';
import AgentTicketsPage from './pages/AgentTicketsPage';
import CustomFieldsAdminPage from './pages/CustomFieldsAdminPage';

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <LandingPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/login',
      element: <LoginPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/reset-password',
      element: <VerifyResetPasswordPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/verify-email',
      element: <VerifyEmailPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/register',
      element: <Registration />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/dashboard',
      element: (
        <PrivateRoute>
          <EnhancedDashboardPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/enhanced-dashboard',
      element: (
        <PrivateRoute>
          <EnhancedDashboardPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/tickets',
      element: (
        <PrivateRoute>
          <TicketsPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/tickets/:id',
      element: (
        <PrivateRoute>
          <TicketDetailPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/create-ticket',
      element: (
        <PrivateRoute>
          <CreateTicketPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/change-password',
      element: (
        <PrivateRoute>
          <ChangePasswordPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/reports',
      element: (
        <PrivateRoute>
          <ReportPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/settings/*',
      element: (
        <PrivateRoute>
          <SettingsPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/custom-fields',
      element: (
        <PrivateRoute>
          <CustomFieldsAdminPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/atrium',
      element: (
        <PrivateRoute>
          <OmnichannelPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    /* {
      path: '/omnichannel/bot-config',
      element: (
        <PrivateRoute>
          <BotConfigPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/settings/bot-config',
      element: (
        <PrivateRoute>
          <BotSettingsPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/settings/bot-config/rules',
      element: (
        <PrivateRoute>
          <BotRulesPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    }, */
    {
      path: '/my-memo',
      element: (
        <PrivateRoute>
          <MyMemoPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/my-workspace',
      element: (
        <PrivateRoute>
          <MyWorkspacePage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/profile',
      element: (
        <PrivateRoute>
          <ProfilePage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/agent-tickets/:userId',
      element: (
        <PrivateRoute>
          <AgentTicketsPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/customers',
      element: (
        <PrivateRoute>
          <CustomersPage />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/customers/:id',
      element: (
        <PrivateRoute>
          <CustomerDetailPage360 />
        </PrivateRoute>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicyPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/terms-of-service',
      element: <TermsOfServicePage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '/contact',
      element: <ContactPage />,
      errorElement: <ErrorBoundary />
    },
    {
      path: '*',
      element: <Navigate to="/dashboard" replace />,
      errorElement: <ErrorBoundary />
    }
  ],
  {
    future: {
      v7_relativeSplatPath: true // Opt-in to relative splat path behavior
    }
  }
);

export default function App() {
  return (
    <ThemeProvider>
      <TenantProvider>
        <LanguageProvider>
          <AuthProvider>
            <DepartmentProvider>
              <FullScreenProvider>
                <TicketRefreshProvider>
                  <ToastProvider>
                    <RouterProvider router={router} />
                  </ToastProvider>
                </TicketRefreshProvider>
              </FullScreenProvider>
            </DepartmentProvider>
          </AuthProvider>
        </LanguageProvider>
      </TenantProvider>
    </ThemeProvider>
  );
}
